<template>
  <b-card-code
    title="Modifier un livreur"
    aria-label="Formulaire de modification du livreur"
  >
    <b-overlay
      :show="isLoading"
      rounded="lg"
      spinner-variant="primary"
      aria-live="polite"
      aria-busy="isLoading"
    >
      <b-card v-if="delivery_man">
        <!-- Info alert for user guidance -->
        <b-alert
          variant="info"
          show
          class="mb-3"
          aria-live="polite"
        >
          <feather-icon
            icon="InfoIcon"
            size="16"
            class="mr-1"
            aria-hidden="true"
          />
          <span>Modifiez les informations du livreur pour garantir la cohérence de votre flotte logistique. Tous les champs marqués d'un astérisque (<span aria-hidden="true">*</span>) sont obligatoires.</span>
        </b-alert>
        <!-- Header with info summary -->
        <div
          class="driver-header mb-3"
          aria-label="Résumé du livreur"
        >
          <div class="d-flex align-items-center">
            <b-avatar
              :text="getInitials(delivery_man.username)"
              variant="light-success"
              size="lg"
              :aria-label="'Avatar du livreur ' + delivery_man.username"
            />
            <div class="ml-2">
              <h4
                class="mb-0"
                aria-label="Nom du livreur"
              >
                {{ delivery_man.username }}
              </h4>
              <div class="d-flex align-items-center">
                <feather-icon
                  icon="MailIcon"
                  size="12"
                  class="text-muted mr-1"
                  aria-hidden="true"
                />
                <span
                  class="text-muted"
                  aria-label="Adresse e-mail professionnelle"
                >{{ delivery_man.email }}</span>
              </div>
            </div>
          </div>
        </div>

        <b-form
          aria-label="Formulaire de modification du livreur"
          @submit.prevent="validateAndSubmit"
        >
          <!-- Informations personnelles du livreur -->
          <div
            class="form-section mb-4"
            aria-label="Section informations personnelles du livreur"
          >
            <h5 class="section-title">
              <feather-icon
                icon="UserIcon"
                class="mr-1"
                size="18"
                aria-hidden="true"
              />
              Informations personnelles du livreur
            </h5>

            <b-row>
              <b-col md="6">
                <b-form-group label-class="form-label-premium"
                  label="Nom complet du livreur *"
                  label-for="username"
                  description="Saisissez le nom complet tel qu'il figure sur les documents officiels. Champ obligatoire."
                >
                  <validation-provider
                    #default="{ errors, valid }"
                    name="nom"
                    rules="required"
                    mode="eager"
                  >
                    <div class="position-relative">
                      <b-form-input
                        id="username"
                        v-model="delivery_man.username"
                        :state="errors.length > 0 ? false : (valid && delivery_man.username ? true : null)"
                        placeholder="Entrez le nom complet du livreur"
                        aria-label="Nom complet du livreur"
                        class="premium-input"
                      />
                      <feather-icon
                        v-if="valid && delivery_man.username"
                        icon="CheckCircleIcon"
                        size="18"
                        class="input-success-icon"
                      />
                    </div>
                    <small
                      v-if="errors[0]"
                      class="text-danger validation-message-animate"
                    >{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>

              <!-- téléphone personnel -->
              <b-col md="6">
                <b-form-group label-class="form-label-premium"
                  label="téléphone personnel"
                  label-for="phone2"
                  description="Saisissez le numéro de téléphone personnel du livreur (8 chiffres, uniquement des chiffres)."
                >
                  <b-form-input class="premium-input"
                    id="phone2"
                    v-model="phone2"
                    placeholder="Entrez le numéro de téléphone personnel"
                    maxlength="8"
                    aria-label="Numéro de téléphone personnel du livreur"
                  />
                </b-form-group>
              </b-col>
              <b-col md="6">
                <b-form-group label-class="form-label-premium"
                  label="téléphone professionnel *"
                  label-for="phone"
                  description="Saisissez le numéro de téléphone du livreur (8 chiffres, uniquement des chiffres). Champ obligatoire."
                >
                  <validation-provider
                    #default="{ errors, valid }"
                    name="téléphone professionnel"
                    :rules="`required|tnPhone|asyncPhone:,${delivery_man.id}`"
                    mode="eager"
                  >
                    <div class="position-relative">
                      <b-input-group>
                        <b-form-input
                          id="phone"
                          v-model="delivery_man.phone"
                          :state="errors.length > 0 ? false : (valid && delivery_man.phone ? true : null)"
                          placeholder="Entrez le téléphone professionnel"
                          aria-label="téléphone professionnel"
                          aria-required="true"
                          aria-describedby="phone-desc"
                          class="premium-input"
                          maxlength="8"
                        />
                        <b-input-group-append is-text>
                          <feather-icon
                            icon="PhoneIcon"
                            size="16"
                            aria-hidden="true"
                          />
                        </b-input-group-append>
                      </b-input-group>
                      <feather-icon
                        v-if="valid && delivery_man.phone"
                        icon="CheckCircleIcon"
                        size="18"
                        class="input-success-icon"
                        style="right: 3.5rem;"
                      />
                    </div>
                    <small
                      v-if="errors[0]"
                      class="text-danger validation-message-animate"
                      aria-live="polite"
                    >{{ errors[0] }}</small>
                    <small
                      id="phone-desc"
                      class="sr-only"
                    >Ce champ est requis pour contacter le livreur en cas d'urgence logistique.</small>
                  </validation-provider>
                </b-form-group>
              </b-col>

              <b-col md="6">
                <b-form-group label-class="form-label-premium"
                  label="Adresse e-mail professionnelle *"
                  label-for="email"
                  description="Saisissez l'adresse e-mail professionnelle du livreur. Champ obligatoire."
                >
                  <validation-provider
                    #default="{ errors, valid }"
                    name="Email"
                    :rules="`required|email|asyncEmail:${delivery_man.id}`"
                    mode="eager"
                  >
                    <b-input-group>
                      <b-form-input
                        id="email"
                        v-model="delivery_man.email"
                        :state="errors.length > 0 ? false : (valid && delivery_man.email ? true : null)"
                        placeholder="Entrez l'adresse e-mail professionnelle"
                        aria-label="Adresse e-mail professionnelle"
                        aria-required="true"
                        aria-describedby="email-desc"
                        class="premium-input"
                      />
                      <b-input-group-append is-text>
                        <feather-icon
                          icon="RefreshCwIcon"
                          size="16"
                          :class="delivery_man.username && delivery_man.username.trim().length > 0 ? 'cursor-pointer text-primary' : 'text-muted cursor-not-allowed'"
                          v-b-tooltip.hover="emailGenerationTooltip"
                          @click="delivery_man.username && delivery_man.username.trim().length > 0 ? generateCompanyEmail() : null"
                        />
                      </b-input-group-append>
                    </b-input-group>
                    <small
                      v-if="errors[0]"
                      class="text-danger validation-message-animate"
                      aria-live="polite"
                    >{{ errors[0] }}</small>
                    <small
                      id="email-desc"
                      class="sr-only"
                    >Ce champ est requis pour la communication officielle avec le livreur.</small>
                  </validation-provider>
                </b-form-group>
              </b-col>

              <b-col md="6">
                <b-form-group label-class="form-label-premium"
                  label="Adresse postale"
                  label-for="address"
                  description="Indiquez l'adresse postale du livreur (optionnel)."
                >
                  <validation-provider
                    #default="{ errors }"
                    name="Adresse"
                  >
                    <b-input-group>
                      <b-form-textarea
                        id="address"
                        v-model="delivery_man.address"
                        :state="errors.length > 0 ? false : null"
                        placeholder="Entrez l'adresse postale du livreur"
                        aria-label="Adresse postale du livreur"
                        aria-describedby="address-desc"
                        rows="3"
                      />
                      <b-input-group-append is-text>
                        <feather-icon
                          icon="MapPinIcon"
                          size="16"
                          aria-hidden="true"
                        />
                      </b-input-group-append>
                    </b-input-group>
                    <b-form-invalid-feedback aria-live="polite">
                      {{ errors[0] }}
                    </b-form-invalid-feedback>
                    <small
                      id="address-desc"
                      class="sr-only"
                    >Ce champ est optionnel et permet de localiser le livreur pour la logistique terrain.</small>
                  </validation-provider>
                </b-form-group>
              </b-col>

              <b-col md="6">
                <b-form-group label-class="form-label-premium"
                  label="Dépôt logistique *"
                  label-for="repository"
                  description="Sélectionnez le dépôt logistique principal du livreur. Champ obligatoire."
                >
                  <validation-provider
                    #default="{ errors, valid }"
                    name="Dépôt"
                    rules="required"
                    mode="eager"
                  >
                    <div class="position-relative">
                      <v-select
                        id="repository"
                        v-model="selectedRepository"
                        :options="repositories"
                        label="name"
                        :reduce="repo => repo.id"
                        placeholder="Sélectionnez un dépôt logistique"
                        class="enhanced-select premium-select"
                        :class="{'is-valid': valid && selectedRepository, 'is-invalid': errors.length > 0}"
                        aria-label="Dépôt logistique principal du livreur"
                        aria-required="true"
                        aria-describedby="repository-desc"
                      />
                      <feather-icon
                        v-if="valid && selectedRepository"
                        icon="CheckCircleIcon"
                        size="18"
                        class="input-success-icon select-icon"
                      />
                    </div>
                    <small
                      v-if="errors[0]"
                      class="text-danger validation-message-animate"
                    >{{ errors[0] }}</small>
                    <small
                      id="repository-desc"
                      class="sr-only"
                    >Ce champ est requis pour l'affectation logistique du livreur.</small>
                  </validation-provider>
                </b-form-group>
              </b-col>

              <!-- Car -->
              <b-col md="6">
                <b-form-group label-class="form-label-premium"
                  label="Véhicule attribué"
                  label-for="car"
                  description="Sélectionnez le véhicule attribué au livreur (optionnel)."
                >
                  <div class="d-flex align-items-center">
                    <v-select
                      id="car"
                      v-model="selectedCar"
                      :options="cars"
                      label="name"
                      :reduce="car => car.id"
                      placeholder="Sélectionnez un véhicule attribué (optionnel)"
                      class="enhanced-select flex-grow-1 mr-1"
                      clearable
                      aria-label="Véhicule attribué au livreur"
                      aria-describedby="car-desc"
                    />
                    <b-button
                      variant="outline-primary"
                      class="btn-icon"
                      v-b-tooltip.hover="'Créer un véhicule rapidement'"
                      @click="$refs.quickCreateCarModal.show()"
                    >
                      <feather-icon icon="ZapIcon" />
                    </b-button>
                  </div>
                  <small
                    id="car-desc"
                    class="sr-only"
                  >Ce champ est optionnel et permet d'associer un véhicule à ce livreur.</small>
                </b-form-group>
              </b-col>

              <!-- Owns Vehicle -->
              <b-col md="6">
                <b-form-group label-class="form-label-premium"
                  label="Propriétaire du Véhicule"
                  label-for="owns_vehicle"
                  description="Le livreur possède-t-il son propre véhicule ?"
                >
                  <b-form-checkbox
                    id="owns_vehicle"
                    v-model="delivery_man.owns_vehicle"
                    switch
                  >
                    {{ delivery_man.owns_vehicle ? 'Oui' : 'Non' }}
                  </b-form-checkbox>
                </b-form-group>
              </b-col>
            </b-row>
          </div>

          <!-- Payment Information Section -->
          <div
            class="form-section mb-4"
            aria-label="Section informations de paiement"
          >
            <h5 class="section-title">
              <feather-icon
                icon="DollarSignIcon"
                class="mr-1"
                size="18"
                aria-hidden="true"
              />
              Informations de Paiement
            </h5>

            <b-row>
              <!-- Payment Type -->
              <b-col md="6">
                <b-form-group label-class="form-label-premium"
                  label="Type de Paiement"
                  label-for="pay_type"
                  description="Sélectionnez le mode de rémunération du livreur."
                >
                  <v-select
                    id="pay_type"
                    v-model="delivery_man.pay_type"
                    :options="paymentTypeOptions"
                    label="label"
                    :reduce="option => option.value"
                    placeholder="Sélectionnez un type de paiement (optionnel)"
                    clearable
                    class="enhanced-select"
                  />
                </b-form-group>
              </b-col>

              <!-- Compensation Amount -->
              <b-col md="6">
                <b-form-group label-class="form-label-premium"
                  :label="compensationLabel"
                  label-for="compensation"
                  :description="compensationDescription"
                >
                  <validation-provider
                    #default="{ errors }"
                    name="Montant"
                    rules="priceDecimal"
                  >
                    <b-form-input class="premium-input"
                      id="compensation"
                      v-model="delivery_man.compensation"
                      :formatter="formatPrice"
                      type="text"
                      inputmode="decimal"
                      placeholder="0.000"
                      :state="errors.length > 0 ? false : null"
                      @keypress="allowOnlyDecimalNumbers"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>
            </b-row>
          </div>

          <!-- Form Actions -->
          <div class="d-flex justify-content-between mt-3">
            <b-button
              variant="outline-secondary"
              :to="{ name: 'delivery-man-list' }"
              aria-label="Annuler et retourner à la liste des livreurs"
            >
              <feather-icon
                icon="XIcon"
                class="mr-1"
                aria-hidden="true"
              />
              Annuler
            </b-button>

            <span id="deliveryman-edit-save-btn-wrapper" class="d-inline-block">
              <b-button
                type="submit"
                variant="primary"
                :disabled="!isFormValid || isSubmitting"
                :class="{ 'not-allowed': !isFormValid || isSubmitting }"
                aria-label="Enregistrer les modifications du livreur"
              >
                <b-spinner
                  v-if="isSubmitting"
                  small
                  class="mr-1"
                  aria-hidden="true"
                />
                <feather-icon
                  v-else
                  icon="SaveIcon"
                  class="mr-1"
                  aria-hidden="true"
                />
                Enregistrer
              </b-button>
            </span>
            <b-tooltip
              v-if="!isFormValid || isSubmitting"
              target="deliveryman-edit-save-btn-wrapper"
              triggers="hover"
              placement="top"
            >
              {{ tooltipMessage }}
            </b-tooltip>
          </div>
        </b-form>
      </b-card>

      <!-- Quick create car modal -->
      <quick-create-car-modal
        ref="quickCreateCarModal"
        @car-created="onCarCreated"
      />
    </b-overlay>
  </b-card-code>
</template>

<script>
import axios from 'axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import BCardCode from '@core/components/b-card-code'
import vSelect from 'vue-select'
import { ValidationProvider } from 'vee-validate'
import {
  BFormInput, BFormGroup, BForm, BRow, BCol, BButton, BAvatar,
  BInputGroup, BInputGroupAppend, BCard, BOverlay, BFormTextarea, BFormInvalidFeedback,
  BFormCheckbox, BSpinner, BAlert,
} from 'bootstrap-vue'
import { required } from '@validations'
import storeAuth from '@/store/store'
import QuickCreateCarModal from '@/components/modals/QuickCreateCarModal.vue'

export default {
  components: {
    BCardCode,
    ValidationProvider,
    BFormInput,
    BFormGroup,
    BForm,
    BRow,
    BCol,
    BButton,
    BInputGroup,
    BInputGroupAppend,
    BCard,
    BOverlay,
    BAvatar,
    BFormTextarea,
    BFormInvalidFeedback,
    BFormCheckbox,
    BSpinner,
    BAlert,
    vSelect,
    QuickCreateCarModal,
  },

  data() {
    return {
      repositories: [],
      cars: [],
      delivery_man: null,
      selectedRepository: null,
      selectedCar: null,
      required,
      isLoading: false,
      isSubmitting: false,
      user: {},
      stats: {
        deliveries: 0,
        successRate: 0,
        avgDeliveryTime: 0,
        rating: 0,
      },
      phone2: '',
      paymentTypeOptions: [
        { value: 'salary', label: 'Salaire' },
        { value: 'commission', label: 'Commission (Par Colis)' },
      ],
    }
  },

  computed: {
    companyDomain() {
      return process.env.VUE_APP_COMPANY_EMAIL_DOMAIN || null
    },

    emailGenerationTooltip() {
      if (!this.companyDomain) {
        return 'Erreur: Variable d\'environnement VUE_APP_COMPANY_EMAIL_DOMAIN non définie'
      }
      if (this.delivery_man.username && this.delivery_man.username.trim().length > 0) {
        return `Générer un email par défaut à partir du nom (@${this.companyDomain}). Modifiable si nécessaire.`
      }
      return 'Veuillez d\'abord saisir le nom du livreur'
    },

    compensationLabel() {
      if (!this.delivery_man) return 'Montant (DT)'

      if (this.delivery_man.pay_type === 'salary') {
        return 'Salaire Mensuel (DT)'
      }
      if (this.delivery_man.pay_type === 'commission') {
        return 'Taux par Colis (DT)'
      }
      return 'Montant (DT)'
    },
    compensationDescription() {
      if (!this.delivery_man) return 'Montant de la rémunération.'

      if (this.delivery_man.pay_type === 'salary') {
        return 'Salaire mensuel fixe du livreur.'
      }
      if (this.delivery_man.pay_type === 'commission') {
        return 'Montant payé par colis livré.'
      }
      return 'Montant de la rémunération.'
    },
    isFormValid() {
      if (!this.delivery_man) return false
      return (
        this.delivery_man.username !== ''
        && this.delivery_man.email !== ''
        && this.delivery_man.phone !== ''
        && this.selectedRepository !== null
      )
    },
    tooltipMessage() {
      if (this.isSubmitting) return 'Enregistrement en cours...'
      if (!this.delivery_man) return 'Chargement des données...'

      const missing = []
      if (!this.delivery_man.username || !this.delivery_man.username.trim()) missing.push('Nom')
      if (!this.delivery_man.email || !this.delivery_man.email.trim()) missing.push('Email')
      if (!this.delivery_man.phone || !this.delivery_man.phone.trim()) missing.push('Téléphone')
      if (!this.selectedRepository) missing.push('Dépôt')

      if (missing.length === 0) return ''
      if (missing.length === 1) {
        return `Veuillez renseigner le champ : ${missing[0]}.`
      }
      if (missing.length <= 3) {
        return `Veuillez remplir les champs obligatoires : ${missing.join(', ')}.`
      }
      return `Veuillez remplir les champs obligatoires manquants (${missing.length} champs).`
    },
  },

  created() {
    this.user = storeAuth.state.user
    this.getRepositories()
    this.getCars()
    this.getDeliveryMan()
  },

  methods: {
    stripAccents(value) {
      if (!value) return ''
      return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    },

    generateCompanyEmail() {
      if (!this.delivery_man.username || !this.delivery_man.username.trim()) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Attention',
            icon: 'AlertCircleIcon',
            text: 'Veuillez d\'abord saisir le nom du livreur.',
            variant: 'warning',
          },
        })
        return
      }

      const username = this.delivery_man.username.trim()
      const cleanUsername = this.stripAccents(username)
        .toLowerCase()
        .replace(/\s+/g, '.')
        .replace(/[^a-z0-9.]/g, '')

      const generatedEmail = `${cleanUsername}@${this.companyDomain}`
      this.delivery_man.email = generatedEmail

      this.$toast({
        component: ToastificationContent,
        props: {
          title: 'Email généré',
          icon: 'CheckCircleIcon',
          text: `Email: ${generatedEmail}`,
          variant: 'success',
        },
      })
    },

    allowOnlyDecimalNumbers(event) {
      const { key, charCode, target } = event
      const currentValue = (target && target.value) || ''

      const isDigit = charCode >= 48 && charCode <= 57
      const isDecimalSeparator = key === '.' || key === ',' || charCode === 46 || charCode === 44

      if (!isDigit && !isDecimalSeparator) {
        event.preventDefault()
        return
      }

      if (isDecimalSeparator && currentValue.includes('.')) {
        event.preventDefault()
      }
    },
    formatPrice(value) {
      if (value === null || value === undefined || value === '') return value
      let cleaned = String(value).replace(',', '.').replace(/[^0-9.]/g, '')
      const firstSepIndex = cleaned.indexOf('.')
      if (firstSepIndex !== -1) {
        const main = cleaned.slice(0, firstSepIndex + 1)
        const rest = cleaned.slice(firstSepIndex + 1).replace(/\./g, '')
        cleaned = main + rest
      }
      return cleaned
    },
    async getRepositories() {
      try {
        const { data } = await axios.get('/api/repositories/')
        this.repositories = data
      } catch (error) {
        this.showToast('danger', 'Erreur lors du chargement des dépôts')
      }
    },

    async onCarCreated(newCar) {
      if (!Array.isArray(this.cars)) {
        this.cars = []
      }
      const formatted = {
        ...newCar,
        name: `${newCar.brand} ${newCar.model || ''}`.trim(),
      }
      this.cars = [...this.cars, formatted]
      await this.$nextTick()
      this.selectedCar = newCar.id
    },

    async getCars() {
      try {
        const { data } = await axios.get('/api/cars/')
        this.cars = (data.results || data).map(car => ({
          ...car,
          name: `${car.brand} ${car.model || ''}`.trim(),
        }))
      } catch (error) {
        this.showToast('danger', 'Erreur lors du chargement des véhicules')
      }
    },

    async getDeliveryMan() {
      this.isLoading = true
      try {
        const { id } = this.$route.params
        const { data } = await axios.get(`/api/auth/users/delivery-men/${id}/`)
        this.delivery_man = data
        // Populate phone2 field with existing value
        this.phone2 = this.delivery_man.phone2 || ''

        // Populate payment info from payment_info object if available
        // Use $set to ensure reactivity
        if (this.delivery_man.payment_info) {
          this.$set(this.delivery_man, 'pay_type', this.delivery_man.payment_info.pay_type)
          this.$set(this.delivery_man, 'compensation', this.delivery_man.payment_info.compensation)
          this.$set(this.delivery_man, 'owns_vehicle', this.delivery_man.payment_info.owns_vehicle)
        } else {
          // Initialize with null values if no payment info exists
          this.$set(this.delivery_man, 'pay_type', null)
          this.$set(this.delivery_man, 'compensation', null)
          this.$set(this.delivery_man, 'owns_vehicle', false)
        }

        if (this.delivery_man.repository) {
          this.selectedRepository = this.delivery_man.repository
        }

        if (this.delivery_man.car) {
          this.selectedCar = this.delivery_man.car
        }
      } catch (error) {
        this.showToast('danger', 'Erreur lors du chargement des données du livreur')
      } finally {
        this.isLoading = false
      }
    },

    async validateAndSubmit() {
      const observer = this.$refs.simpleRules
      if (!observer) {
        await this.editDeliveryMan()
        return
      }

      const success = await observer.validate()
      if (success) {
        await this.editDeliveryMan()
      } else {
        this.showToast('warning', 'Veuillez corriger les erreurs dans le formulaire')
      }
    },

    async editDeliveryMan() {
      const parsePrice = val => {
        if (val === null || val === undefined || val === '') return null
        const normalized = String(val).replace(',', '.')
        const num = parseFloat(normalized)
        return Number.isNaN(num) ? val : num
      }
      this.isSubmitting = true

      try {
        // Send all editable fields from the form
        const payload = {
          username: this.delivery_man.username,
          email: this.delivery_man.email,
          phone: this.delivery_man.phone,
          phone2: this.phone2,
          address: this.delivery_man.address,
          repository: this.selectedRepository,
          car: this.selectedCar,
          pay_type: this.delivery_man.pay_type,
          compensation: parsePrice(this.delivery_man.compensation),
          owns_vehicle: this.delivery_man.owns_vehicle || false,
        }

        await axios.put(
          `/api/auth/users/delivery-men/update/${this.delivery_man.id}/`,
          payload,
        )

        this.showToast('success', 'Livreur modifié avec succès')
        this.$router.push({ name: 'delivery-man-list' })
      } catch (error) {
        this.showToast('danger', error.response?.data?.message || 'Erreur lors de la modification du livreur')
      } finally {
        this.isSubmitting = false
      }
    },

    showToast(variant, text) {
      let title
      let icon

      if (variant === 'success') {
        title = 'Succès'
        icon = 'CheckCircleIcon'
      } else if (variant === 'warning') {
        title = 'Attention'
        icon = 'AlertTriangleIcon'
      } else {
        title = 'Erreur'
        icon = 'XCircleIcon'
      }

      this.$toast({
        component: ToastificationContent,
        props: {
          title,
          icon,
          text,
          variant,
        },
      })
    },

    getInitials(name) {
      if (!name) return '??'
      return name
        .split(' ')
        .map(word => word[0])
        .join('')
        .toUpperCase()
    },

    truncate(text, length) {
      if (!text) return ''
      return text.length > length ? `${text.substring(0, length)}...` : text
    },
  },
}
</script>

<style lang="scss" scoped>
@import "@core/scss/vue/libs/vue-select.scss";

.cursor-pointer {
  cursor: pointer;
}

.driver-header {
  padding: 1rem;
  border-radius: 0.5rem;
  background-color: #f8f8f8;
}

.form-section {
  padding: 1.5rem;
  background-color: #f8f9fa;
  border-radius: 0.5rem;

  .section-title {
    display: flex;
    align-items: center;
    margin-bottom: 1.5rem;
    color: #5e5873;
    font-weight: 500;
  }
}

.form-actions {
  margin-top: 2rem;
}

// Statistics styles
.statistics-row {
  .stat-card {
    display: flex;
    align-items: center;
    padding: 0.75rem;
    border-radius: 0.5rem;
    background-color: white;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
    height: 100%;

    .stat-icon {
      width: 46px;
      height: 46px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-right: 0.75rem;
    }

    .stat-details {
      flex-grow: 1;

      .stat-value {
        margin-bottom: 0.25rem;
        font-size: 1.25rem;
        font-weight: 600;
      }

      small {
        color: #6e6b7b;
        font-size: 0.75rem;
      }
    }
  }
}

// Enhance select styling
::v-deep .vs__dropdown-toggle {
  padding: 0.4rem;
  border-color: #d8d6de;

  &:hover {
    border-color: #C62828;
  }
}

::v-deep .vs--open .vs__dropdown-toggle {
  border-color: #C62828;
}

::v-deep .vs__search::placeholder {
  color: #b9b9c3;
}

::v-deep .vs__selected {
  color: #5e5873;
}

// Input focus states
.form-control:focus, .custom-select:focus {
  border-color: #C62828;
}

.input-group-text {
  background-color: #f8f8f8;
  border-color: #d8d6de;
}

.input-group-merge .form-control:focus ~ .input-group-append .input-group-text {
  border-color: #C62828;
}

// Enhanced v-select styling to ensure it shows properly
::v-deep .enhanced-select {
  .vs__dropdown-toggle {
    padding: 0.4rem;
    border: 1px solid #d8d6de;
    background-color: #fff;

    &:hover {
      border-color: #C62828;
    }
  }

  .vs__selected {
    padding: 2px 6px;
    margin: 2px;
  }

  .vs__dropdown-menu {
    min-width: 100%;
    width: auto;
    padding: 0;
    border-radius: 0.357rem;
    box-shadow: 0 5px 25px rgba(0, 0, 0, 0.1);
  }
}

// Premium Input Styling with Real-time Validation
.premium-input {
  transition: all 0.3s ease;
  padding: 0.75rem 1rem;
  border: 2px solid #e2e8f0;
  border-radius: 8px;

  &:focus {
    border-color: #C62828;
    box-shadow: 0 0 0 3px rgba(13, 67, 153, 0.15);
  }

  &.is-valid {
    border-color: #28c76f !important;
    background-color: #f0fdf4;
    padding-right: 3rem;

    &:focus {
      border-color: #28c76f !important;
      box-shadow: 0 0 0 3px rgba(40, 199, 111, 0.15);
    }
  }

  &.is-invalid {
    border-color: #ea5455 !important;
    background-color: #fef2f2;

    &:focus {
      border-color: #ea5455 !important;
      box-shadow: 0 0 0 3px rgba(234, 84, 85, 0.15);
    }
  }
}

.input-success-icon {
  position: absolute;
  right: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: #28c76f;
  pointer-events: none;
  z-index: 5;
  animation: checkFadeIn 0.3s ease;

  &.textarea-icon {
    top: 1rem;
    transform: none;
  }

  &.select-icon {
    right: 3rem;
  }

  &.password-icon {
    right: 3.5rem;
  }
}

@keyframes checkFadeIn {
  from {
    opacity: 0;
    transform: translateY(-50%) scale(0.5);
  }
  to {
    opacity: 1;
    transform: translateY(-50%) scale(1);
  }
}

.validation-message-animate {
  display: inline-flex;
  align-items: center;
  animation: fadeInDown 0.3s ease;
  margin-top: 0.25rem;
}

@keyframes fadeInDown {
  from {
    opacity: 0;
    transform: translateY(-4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

// V-Select Real-time Validation
::v-deep .premium-select {
  &.is-valid .vs__dropdown-toggle {
    border: 2px solid #28c76f !important;
    background-color: #f0fdf4;
  }

  &.is-invalid .vs__dropdown-toggle {
    border: 2px solid #ea5455 !important;
    background-color: #fef2f2;
  }
}

// Submit Button Cursor Feedback
.btn.not-allowed,
.btn:disabled {
  cursor: not-allowed !important;
  opacity: 0.6 !important;
  pointer-events: auto !important;
}
</style>
